import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
    Menu,
    X,
    ChevronDown,
    ChevronRight,
    ChevronLeft,
    GraduationCap,
    Users,
    BookOpen,
    Settings,
    ArrowRight,
    FileCog,
} from "lucide-react";
import { MARKETING_BASE_URL } from "@/lib/constants/routes";
import { brandConfig, mainNavigation, loginOptions } from "@/data/shared";
const checkoutLink = `${MARKETING_BASE_URL}/pricing`;
export function Navigation() {
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    // Mobile drill-down menu state
    const [activePanel, setActivePanel] = useState<string | null>(null);
    const [expandedItem, setExpandedItem] = useState<string | null>(null);
    const [isAtTop, setIsAtTop] = useState(true);
    const [mounted, setMounted] = useState(false);
    const location = useLocation();
    const pathname = location.pathname;

    // Icon mapping for login options
    const iconMap = {
        GraduationCap,
        Users,
        BookOpen,
        Settings,
        FileCog,
    };

    // Function to check if a route is active
    const isActiveRoute = (href: string) => {
        if (!mounted || !href) return false;
        // Exact match for home route
        if (href === "/" && pathname === "/") return true;
        // Prefix match for other routes, but avoid matching root path
        if (href !== "/" && pathname.startsWith(href)) return true;
        return false;
    };

    // Handle mounting
    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!mounted) return;

        const controlNavbar = () => {
            const currentScrollY = window.scrollY;

            // Update if we're at the top (for shadow effect)
            setIsAtTop(currentScrollY < 10);

            setLastScrollY(currentScrollY);
        };

        // Set initial state
        setIsAtTop(window.scrollY < 10);

        const throttledControlNavbar = throttle(controlNavbar, 100);
        window.addEventListener("scroll", throttledControlNavbar);

        return () => window.removeEventListener("scroll", throttledControlNavbar);
    }, [lastScrollY, mounted]);

    // Throttle function to limit scroll event frequency
    function throttle(func: () => void, delay: number) {
        let timeoutId: ReturnType<typeof setTimeout>;
        let lastExecTime = 0;
        return function () {
            const currentTime = Date.now();

            if (currentTime - lastExecTime > delay) {
                func();
                lastExecTime = currentTime;
            } else {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    func();
                    lastExecTime = Date.now();
                }, delay - (currentTime - lastExecTime));
            }
        };
    }

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        setOpenDropdown(null);
        setActivePanel(null);
        setExpandedItem(null);
    };

    const toggleDropdown = (label: string) => {
        setOpenDropdown(openDropdown === label ? null : label);
    };

    return (
        <motion.header
            initial={{ y: 0, opacity: 1 }}
            animate={{
                y: 0,
                opacity: 1,
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black transition-shadow duration-300"
        >
            <nav className="container mx-auto px-4">
                <div className="flex items-center justify-between h-16">
                    <div className="flex flex-row items-center justify-center space-x-4 md:space-x-8">
                        {/* Logo */}
                        <a href={MARKETING_BASE_URL}>
                            <img
                                src="/logo.webp"
                                alt="Nicole the Math Lady - Online Math Tutoring and Saxon Math Curriculum"
                                className="h-9 md:h-11 w-auto"
                                loading="eager"
                                width={150}
                                height={44}
                            />
                        </a>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
                            {mainNavigation.map((item) => (
                                <div key={item.label} className="relative group">
                                    {item.children || item.sections ? (
                                        <>
                                            <button
                                                className="flex items-center space-x-1 text-gray-700 hover:text-green-700 transition-colors duration-200 py-2"
                                                onMouseEnter={() => setOpenDropdown(item.label)}
                                                onMouseLeave={() => setOpenDropdown(null)}
                                            >
                                                <span>{item.label}</span>
                                                <ChevronDown className="w-4 h-4" />
                                            </button>

                                            {/* Dropdown Menu */}
                                            <AnimatePresence>
                                                {openDropdown === item.label && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: 10 }}
                                                        transition={{ duration: 0.2 }}
                                                        className={`absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-3 ${item.sections ? "w-80" : "w-64"
                                                            }`}
                                                        onMouseEnter={() => setOpenDropdown(item.label)}
                                                        onMouseLeave={() => setOpenDropdown(null)}
                                                    >
                                                        {item.sections ? (
                                                            // Multi-section dropdown (like "More")
                                                            <div className="space-y-4">
                                                                {item.sections.map((section, sectionIndex) => (
                                                                    <div key={sectionIndex}>
                                                                        {section.title && (
                                                                            <div className="px-4 py-1">
                                                                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                                                                    {section.title}
                                                                                </h4>
                                                                            </div>
                                                                        )}
                                                                        <div className="space-y-1">
                                                                            {section.items.map((sectionItem) => (
                                                                                <a
                                                                                    key={sectionItem.label}
                                                                                    href={sectionItem.href}
                                                                                    onClick={() => setOpenDropdown(null)}
                                                                                    className="flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
                                                                                >
                                                                                    <span>{sectionItem.label}</span>
                                                                                    {sectionItem.badge && (
                                                                                        <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
                                                                                            {sectionItem.badge}
                                                                                        </span>
                                                                                    )}
                                                                                </a>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : item.children ? (
                                                            // Simple dropdown (like "Curriculum")
                                                            <div className="py-1">
                                                                {item.children.map((child) => (
                                                                    <a
                                                                        key={child.label}
                                                                        href={child.href}
                                                                        onClick={() => setOpenDropdown(null)}
                                                                        className="block px-4 py-3 text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
                                                                    >
                                                                        {child.label}
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </>
                                    ) : (
                                        <a
                                            href={item.href}
                                            className={
                                                mounted && isActiveRoute(item.href || "#")
                                                    ? "transition-colors duration-200 font-semibold text-green-700"
                                                    : "transition-colors duration-200 text-gray-700 hover:text-green-700"
                                            }
                                        >
                                            {item.label}
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Desktop Action Buttons */}
                    <div className="hidden lg:flex items-center space-x-2 xl:space-x-4">
                        <div className="relative">
                            <Button
                                variant="ghost"
                                className="text-gray-700 hover:text-green-700 px-3 xl:px-4"
                                onMouseEnter={() => setOpenDropdown("Login")}
                                onMouseLeave={() => setOpenDropdown(null)}
                            >
                                Login
                            </Button>

                            {/* Login Dropdown */}
                            <AnimatePresence>
                                {openDropdown === "Login" && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-3"
                                        onMouseEnter={() => setOpenDropdown("Login")}
                                        onMouseLeave={() => setOpenDropdown(null)}
                                    >
                                        <div className="px-4 py-2">
                                            <h4 className="text-sm font-medium text-gray-500">
                                                Login as...
                                            </h4>
                                        </div>
                                        <div className="space-y-1">
                                            {loginOptions.map((option) => {
                                                const IconComponent = option.icon
                                                    ? iconMap[option.icon as keyof typeof iconMap]
                                                    : null;
                                                return (
                                                    <a
                                                        key={option.label}
                                                        href={option.href}
                                                        className="flex items-center px-4 py-3 text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
                                                    >
                                                        {IconComponent && (
                                                            <IconComponent className="w-5 h-5 mr-3 text-gray-400" />
                                                        )}
                                                        <span>{option.label}</span>
                                                        <ArrowRight className="w-4 h-4 ml-auto text-gray-400" />
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <a href={checkoutLink ? checkoutLink : "#"}>
                            <Button>Get Started</Button>
                        </a>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={toggleMobileMenu}
                        className="lg:hidden p-2 relative w-10 h-10 flex items-center justify-center group"
                        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                    >
                        <div className="w-6 h-5 relative flex flex-col justify-between">
                            <span
                                className={`block h-0.5 w-full bg-gray-700 rounded-full transition-all duration-300 ease-in-out origin-center ${isMobileMenuOpen ? "translate-y-2.25 rotate-45" : ""
                                    }`}
                            />
                            <span
                                className={`block h-0.5 w-full bg-gray-700 rounded-full transition-all duration-300 ease-in-out ${isMobileMenuOpen ? "opacity-0 scale-x-0" : "opacity-100"
                                    }`}
                            />
                            <span
                                className={`block h-0.5 w-full bg-gray-700 rounded-full transition-all duration-300 ease-in-out origin-center ${isMobileMenuOpen ? "-translate-y-2.25 -rotate-45" : ""
                                    }`}
                            />
                        </div>
                    </button>
                </div>

                {/* Mobile Menu — Full-screen drill-down panel */}
                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                            className="lg:hidden fixed inset-x-0 top-16 bottom-0 bg-white z-50 flex flex-col border-t border-gray-200"
                        >
                            <div className="flex-1 overflow-y-auto overflow-x-hidden relative">
                                {/* Sliding container — uses CSS transform for smooth animation */}
                                <div
                                    className="flex transition-transform duration-300 ease-in-out h-full"
                                    style={{
                                        width: "200%",
                                        transform: activePanel
                                            ? "translateX(-50%)"
                                            : "translateX(0)",
                                    }}
                                >
                                    {/* Level 1 — Main menu */}
                                    <div className="w-1/2 h-full overflow-y-auto">
                                        <div className="container mx-auto">
                                            <div className="divide-y divide-gray-100">
                                                {mainNavigation.map((item) => {
                                                    const hasChildren = !!(
                                                        item.children || item.sections
                                                    );
                                                    if (hasChildren) {
                                                        return (
                                                            <button
                                                                key={item.label}
                                                                onClick={() => {
                                                                    setActivePanel(item.label);
                                                                    setExpandedItem(null);
                                                                }}
                                                                className="flex items-center justify-between w-full px-4 py-4 text-left hover:bg-gray-50 transition-colors duration-150"
                                                            >
                                                                <span className="text-[15px] font-semibold text-gray-900">
                                                                    {item.label}
                                                                </span>
                                                                <ChevronRight className="w-5 h-5 text-gray-400" />
                                                            </button>
                                                        );
                                                    }
                                                    return (
                                                        <a
                                                            key={item.label}
                                                            href={item.href}
                                                            onClick={() => setIsMobileMenuOpen(false)}
                                                            className={`flex items-center justify-between px-4 py-4 transition-colors duration-150 ${mounted && isActiveRoute(item.href || "#")
                                                                ? "text-green-700 font-bold bg-green-50/50"
                                                                : "text-gray-900 hover:bg-gray-50"
                                                                }`}
                                                        >
                                                            <span className="text-[15px] font-semibold">
                                                                {item.label}
                                                            </span>
                                                        </a>
                                                    );
                                                })}
                                            </div>

                                            {/* Login section */}
                                            <div className="border-t border-gray-200 mt-2">
                                                <button
                                                    onClick={() => {
                                                        setActivePanel("Login");
                                                        setExpandedItem(null);
                                                    }}
                                                    className="flex items-center justify-between w-full px-4 py-4 text-left hover:bg-gray-50 transition-colors duration-150"
                                                >
                                                    <span className="text-[15px] font-semibold text-gray-900">
                                                        Login
                                                    </span>
                                                    <ChevronRight className="w-5 h-5 text-gray-400" />
                                                </button>
                                            </div>

                                            {/* Get Started CTA */}
                                            <div className="px-4 py-4">
                                                <a
                                                    href={checkoutLink || "#"}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                >
                                                    <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                                                        Get Started
                                                    </Button>
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Level 2 — Sub-menu panel */}
                                    <div className="w-1/2 h-full overflow-y-auto">
                                        <div className="container mx-auto">
                                            {/* Back button */}
                                            <button
                                                onClick={() => {
                                                    setActivePanel(null);
                                                    setExpandedItem(null);
                                                }}
                                                className="flex items-center w-full px-4 py-3 text-gray-600 hover:bg-gray-50 border-b border-gray-100 transition-colors duration-150"
                                            >
                                                <ChevronLeft className="w-4 h-4 mr-1" />
                                                <span className="text-sm font-medium">
                                                    {activePanel}
                                                </span>
                                            </button>

                                            {/* Render sub-items based on panel type */}
                                            {activePanel === "Login" ? (
                                                // Login options
                                                <div className="divide-y divide-gray-100">
                                                    {loginOptions.map((option) => {
                                                        const IconComponent = option.icon
                                                            ? iconMap[option.icon as keyof typeof iconMap]
                                                            : null;
                                                        return (
                                                            <a
                                                                key={option.label}
                                                                href={option.href}
                                                                onClick={() => setIsMobileMenuOpen(false)}
                                                                className="flex items-center px-4 py-4 text-gray-800 hover:bg-gray-50 transition-colors duration-150"
                                                            >
                                                                {IconComponent && (
                                                                    <IconComponent className="w-5 h-5 mr-3 text-gray-400" />
                                                                )}
                                                                <span className="text-[15px] font-medium">
                                                                    {option.label}
                                                                </span>
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                // Navigation sub-items
                                                (() => {
                                                    const navItem = mainNavigation.find(
                                                        (item) => item.label === activePanel,
                                                    );
                                                    if (!navItem) return null;

                                                    // Items with sections (like "More") — each section is a drillable item
                                                    if (navItem.sections) {
                                                        return (
                                                            <div className="divide-y divide-gray-100">
                                                                {navItem.sections.map(
                                                                    (section, sectionIndex) => {
                                                                        const isExpanded =
                                                                            expandedItem ===
                                                                            (section.title ||
                                                                                `section-${sectionIndex}`);
                                                                        const sectionKey =
                                                                            section.title ||
                                                                            `section-${sectionIndex}`;

                                                                        return (
                                                                            <div key={sectionIndex}>
                                                                                {/* Section title as expandable button */}
                                                                                <button
                                                                                    onClick={() =>
                                                                                        setExpandedItem(
                                                                                            isExpanded ? null : sectionKey,
                                                                                        )
                                                                                    }
                                                                                    className={`flex items-center justify-between w-full px-4 py-4 text-left transition-colors duration-150 ${isExpanded
                                                                                        ? "bg-gray-50 border-l-3 border-green-600"
                                                                                        : "hover:bg-gray-50"
                                                                                        }`}
                                                                                >
                                                                                    <span className="text-[15px] font-semibold text-gray-900">
                                                                                        {section.title || "Items"}
                                                                                    </span>
                                                                                    {isExpanded ? (
                                                                                        <ChevronDown className="w-5 h-5 text-gray-400" />
                                                                                    ) : (
                                                                                        <ChevronRight className="w-5 h-5 text-gray-400" />
                                                                                    )}
                                                                                </button>
                                                                                {/* Section items — inline expand */}
                                                                                <AnimatePresence>
                                                                                    {isExpanded && (
                                                                                        <motion.div
                                                                                            initial={{
                                                                                                height: 0,
                                                                                                opacity: 0,
                                                                                            }}
                                                                                            animate={{
                                                                                                height: "auto",
                                                                                                opacity: 1,
                                                                                            }}
                                                                                            exit={{ height: 0, opacity: 0 }}
                                                                                            transition={{ duration: 0.2 }}
                                                                                            className="overflow-hidden bg-gray-50"
                                                                                        >
                                                                                            {section.items.map(
                                                                                                (sectionItem) => (
                                                                                                    <a
                                                                                                        key={sectionItem.label}
                                                                                                        href={sectionItem.href}
                                                                                                        onClick={() =>
                                                                                                            setIsMobileMenuOpen(false)
                                                                                                        }
                                                                                                        className="flex items-center justify-between px-8 py-3 text-gray-700 hover:text-green-700 hover:bg-gray-100/50 transition-colors duration-150"
                                                                                                    >
                                                                                                        <span className="text-[14px] font-medium">
                                                                                                            {sectionItem.label}
                                                                                                        </span>
                                                                                                        {sectionItem.badge && (
                                                                                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                                                                                                {sectionItem.badge}
                                                                                                            </span>
                                                                                                        )}
                                                                                                    </a>
                                                                                                ),
                                                                                            )}
                                                                                        </motion.div>
                                                                                    )}
                                                                                </AnimatePresence>
                                                                            </div>
                                                                        );
                                                                    },
                                                                )}
                                                            </div>
                                                        );
                                                    }

                                                    // Items with children (like "Curriculum")
                                                    if (navItem.children) {
                                                        return (
                                                            <div className="divide-y divide-gray-100">
                                                                {navItem.children.map((child) => {
                                                                    const hasGrandchildren = !!(
                                                                        child.children && child.children.length > 0
                                                                    );
                                                                    const isExpanded =
                                                                        expandedItem === child.label;

                                                                    return (
                                                                        <div key={child.label}>
                                                                            {hasGrandchildren ? (
                                                                                <>
                                                                                    <button
                                                                                        onClick={() =>
                                                                                            setExpandedItem(
                                                                                                isExpanded ? null : child.label,
                                                                                            )
                                                                                        }
                                                                                        className={`flex items-center justify-between w-full px-6 py-4 text-left transition-colors duration-150 ${isExpanded
                                                                                            ? "bg-gray-50 border-l-3 border-green-600"
                                                                                            : "hover:bg-gray-50"
                                                                                            }`}
                                                                                    >
                                                                                        <span className="text-[15px] font-semibold text-gray-900">
                                                                                            {child.label}
                                                                                        </span>
                                                                                        {isExpanded ? (
                                                                                            <ChevronDown className="w-5 h-5 text-gray-400" />
                                                                                        ) : (
                                                                                            <ChevronRight className="w-5 h-5 text-gray-400" />
                                                                                        )}
                                                                                    </button>
                                                                                    {/* Level 3 — Inline expand */}
                                                                                    <AnimatePresence>
                                                                                        {isExpanded && (
                                                                                            <motion.div
                                                                                                initial={{
                                                                                                    height: 0,
                                                                                                    opacity: 0,
                                                                                                }}
                                                                                                animate={{
                                                                                                    height: "auto",
                                                                                                    opacity: 1,
                                                                                                }}
                                                                                                exit={{ height: 0, opacity: 0 }}
                                                                                                transition={{ duration: 0.2 }}
                                                                                                className="overflow-hidden bg-gray-50"
                                                                                            >
                                                                                                {child.children!.map(
                                                                                                    (grandchild) => (
                                                                                                        <a
                                                                                                            key={grandchild.label}
                                                                                                            href={grandchild.href}
                                                                                                            onClick={() =>
                                                                                                                setIsMobileMenuOpen(
                                                                                                                    false,
                                                                                                                )
                                                                                                            }
                                                                                                            className="block px-8 py-2.5 text-[14px] text-gray-600 hover:text-green-700 transition-colors duration-150"
                                                                                                        >
                                                                                                            {grandchild.label}
                                                                                                        </a>
                                                                                                    ),
                                                                                                )}
                                                                                            </motion.div>
                                                                                        )}
                                                                                    </AnimatePresence>
                                                                                </>
                                                                            ) : (
                                                                                <a
                                                                                    href={child.href}
                                                                                    onClick={() =>
                                                                                        setIsMobileMenuOpen(false)
                                                                                    }
                                                                                    className="flex items-center justify-between px-6 py-4 text-gray-900 hover:bg-gray-50 transition-colors duration-150"
                                                                                >
                                                                                    <span className="text-[15px] font-semibold">
                                                                                        {child.label}
                                                                                    </span>
                                                                                </a>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    }

                                                    return null;
                                                })()
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </nav>
        </motion.header>
    );
}
